import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { WithTranslation } from 'react-i18next'

import get from 'lodash/get'
import isNil from 'lodash/isNil'
import defaultTo from 'lodash/defaultTo'

import { ChildProps } from 'react-apollo'
import { InputProps } from './createProjectMutation'

import { countryOptions } from 'data'

import PageLayout from 'components/layout/pageLayout'
import Head from 'components/layout/head'
import Button from 'components/ui/button'
import Input from 'components/ui/input'
import Select from 'components/ui/select'
import TextArea from 'components/ui/textArea'

interface NewProjectProps
  extends ChildProps<InputProps, Response>,
    RouteComponentProps,
    WithTranslation {
  data: any
}

interface IErrorObject {
  [k: string]: string | null
}

type NewProjectState = {
  errors: IErrorObject | null
  success: boolean
  projectNumber: string | null
  projectName: string | null
  company: string | null
  hourlyRate: number | null
  tax: number | null
  date: Date | Date[] | null
  contact: string | null
  street: string | null
  zipCode: string | null
  city: string | null
  country: string | null
  description: string | null
}

class NewProject extends Component<NewProjectProps, NewProjectState> {
  constructor(props: NewProjectProps) {
    super(props)
    this.state = {
      errors: null,
      success: false,
      projectNumber: null,
      projectName: null,
      company: null,
      hourlyRate: null,
      tax: null,
      date: null,
      contact: null,
      street: null,
      zipCode: null,
      city: null,
      country: 'AT',
      description: null,
    }
  }

  getValue = (key: string) => {
    return get(this.state, key)
  }

  handleSave = () => {
    const { history, mutate } = this.props

    const errors = {
      api: null,
      projectNumber: isNil(this.getValue('projectNumber')) ? 'Error Message.' : null,
      projectName: isNil(this.getValue('projectName')) ? 'Error Message.' : null,
      contact: isNil(this.getValue('contact')) ? 'Error Message.' : null,
      tax: isNil(this.getValue('tax')) ? 'Error Message.' : null,
      street: isNil(this.getValue('street')) ? 'Error Message.' : null,
      city: isNil(this.getValue('city')) ? 'Error Message.' : null,
      country: isNil(this.getValue('country')) ? 'Error Message.' : null,
      zipCode: isNil(this.getValue('zipCode')) ? 'Error Message.' : null,
    }

    const hasErrors = Object.values(errors).find((error) => {
      if (error) return true
      return false
    })

    if (hasErrors) {
      this.setState({ errors, success: false })
    } else {
      if (mutate) {
        mutate({
          variables: { ...this.state },
        })
          .then((res) => {
            // Project create was successful
            if (get(res, 'data.createProject')) {
              this.setState({ errors: null, success: true })
              history.push('/projects')
            } else {
              this.setState({
                errors: {
                  api: 'Projekt konnte nicht angelegt werden',
                },
                success: false,
              })
            }
          })
          .catch((e) => {
            const statusCode = JSON.parse(JSON.stringify(e)).graphQLErrors[0].message.statusCode

            let errorMessage = null
            switch (statusCode) {
              case 409:
                errorMessage = 'Projektnummer wird bereits verwendet'
                break
              default:
                errorMessage = 'Projekt konnte nicht angelegt werden'
                break
            }

            this.setState({
              errors: {
                api: errorMessage,
              },
              success: false,
            })
          })
      }
    }
  }

  render() {
    const { errors, success } = this.state

    return (
      <PageLayout pageTitle="Projekt anlegen">
        <Head
          headline="Projekt anlegen"
          subheadline="Zurück zur Übersicht"
          linkTo="/projects"
          error={errors ? true : false}
          message={
            errors
              ? defaultTo(get(errors, 'api'), 'Pflichtfelder ausfüllen!')
              : success
              ? 'Änderungen gespeichert!'
              : undefined
          }
          actions={
            <Button primary onClick={this.handleSave}>
              Speichern
            </Button>
          }
        />
        <div className="px-48 my-65">
          <div className="flex">
            <div className="w-4/12 flex items-end">
              <Input
                name="projectNumber"
                label="Nummer"
                required
                value={defaultTo(this.getValue('projectNumber'), '')}
                leftAlign
                error={errors && errors.projectNumber ? errors.projectNumber : undefined}
                onChange={(value) => this.setState({ projectNumber: value ? String(value) : null })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="projectName"
                label="BV-/Projektname"
                required
                value={defaultTo(this.getValue('projectName'), '')}
                leftAlign
                error={errors && errors.projectName ? errors.projectName : undefined}
                onChange={(value) => this.setState({ projectName: value ? String(value) : null })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="contact"
                label="Ansprechpartner"
                required
                value={defaultTo(this.getValue('contact'), '')}
                leftAlign
                error={errors && errors.contact ? errors.contact : undefined}
                onChange={(value) => this.setState({ contact: value ? String(value) : null })}
              />
            </div>
          </div>
          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="street"
                label="Straße"
                required
                leftAlign
                value={defaultTo(this.getValue('street'), '')}
                error={errors && errors.street ? errors.street : undefined}
                onChange={(value) => this.setState({ street: value ? String(value) : null })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                type="number"
                name="zipCode"
                required
                min={0}
                label="PLZ"
                leftAlign
                value={defaultTo(this.getValue('zipCode'), '')}
                error={errors && errors.zipCode ? errors.zipCode : undefined}
                onChange={(value) => this.setState({ zipCode: value ? String(value) : null })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="city"
                label="Ort"
                required
                leftAlign
                value={defaultTo(this.getValue('city'), '')}
                error={errors && errors.city ? errors.city : undefined}
                onChange={(value) => this.setState({ city: value ? String(value) : null })}
              />
            </div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Select
                label="Land"
                required
                options={countryOptions}
                defaultValue={defaultTo(this.getValue('country'), 'AT')}
                error={errors && errors.country ? errors.country : undefined}
                onChange={(option) => this.setState({ country: String(option.value) })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="company"
                label="Büro/Firma"
                value={defaultTo(this.getValue('company'), '')}
                leftAlign
                error={errors && errors.company ? errors.company : undefined}
                onChange={(value) => this.setState({ company: value ? String(value) : null })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="date"
                type="date"
                value={this.getValue('date')}
                label="Datum"
                leftAlign
                error={errors && errors.date ? errors.date : undefined}
                onDateChange={(value) => this.setState({ date: value ? value : null })}
              />
            </div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                type="number"
                name="hourlyRate"
                label="Stundenlohn"
                unit="EUR"
                value=""
                onChange={(value) => this.setState({ hourlyRate: Number(value) })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                type="number"
                name="tax"
                label="Mehrwertsteuer"
                unit="%"
                required
                value=""
                error={errors && errors.tax ? errors.tax : undefined}
                onChange={(value) =>
                  this.setState({
                    tax: Number(value),
                  })
                }
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-full">
              <TextArea
                name="description"
                label="Beschreibung"
                error={errors && errors.description ? errors.description : undefined}
                onChange={(value) => this.setState({ description: value ? String(value) : null })}
              />
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }
}

export default NewProject
